import React, { useState, useRef, useEffect, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Loader, Send } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import ParticleBackground from './ParticleBackground';
import TopNavBar from './TopNavBar';
import ReachCurveChart from './ReachCurveChart';
import { sendADKMessageStreaming, ADK_APP_NAME, getSessionInfo, ADK_API_BASE_URL, downloadProtectedFile, isAuthExpiredError, planGoneError } from '../config/api';
import { campaignManagerGenerateTrigger, isProtectedDownloadHref, isTriggerCommand } from '../lib/triggers';
import ScenarioCards, { parseScenarios, stripScenarios } from './ScenarioCards';
import { Message, PlatformType } from '../types';
import '../styles/ChatInterfaceNew.css';

interface AgentWorkspaceProps {
  sessionId: string;
  platform: PlatformType;
  userEmail: string | null;
  onLogout: () => void;
  selectedGoogleAdsCustomer: string;
  selectedAdvertiserId: string;
  completedSteps?: number[];
  onNavigateToStep?: (step: number) => void;
  onStepComplete?: (step: number) => void;
}

// Helper function to extract and parse reach curve chart data from markdown
const extractChartData = (content: string) => {
  const chartRegex = /```json:reach-curve\n([\s\S]*?)\n```/g;
  const matches = Array.from(content.matchAll(chartRegex));

  const charts: any[] = [];
  let cleanedContent = content;

  matches.forEach((match) => {
    try {
      const chartData = JSON.parse(match[1]);
      charts.push(chartData);
      // Remove the chart block from markdown
      cleanedContent = cleanedContent.replace(match[0], '');
    } catch (e) {
      console.error('Failed to parse chart data:', e);
    }
  });

  return { charts, cleanedContent };
};

// Extract generated-file download links (campaign-manager output) from a message.
const extractDownloadLinks = (content: string): { filename: string; href: string }[] => {
  const re = /\/download-file\?session_id=[^)\s"'`]+/g;
  const out: { filename: string; href: string }[] = [];
  for (const path of content.match(re) || []) {
    const m = path.match(/filename=([^&)\s]+)/);
    const filename = m ? decodeURIComponent(m[1]) : 'file';
    out.push({ filename, href: `${ADK_API_BASE_URL}${path}` });
  }
  return out;
};

// A generated-file link in the chat needs the bearer token, which a plain
// <a href> navigation cannot send: the browser got a 401. So the click
// fetches it with credentials, like the download buttons do.
const markdownComponents = {
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => {
    if (!isProtectedDownloadHref(href)) {
      return <a href={href}>{children}</a>;
    }
    const m = (href ?? '').match(/filename=([^&]+)/);
    let filename = 'file';
    try {
      if (m) filename = decodeURIComponent(m[1]);
    } catch {
      // A stray "%" in a name must not break rendering the chat.
      filename = m![1];
    }
    return (
      <a
        href={href}
        onClick={(e) => {
          e.preventDefault();
          downloadProtectedFile(`${ADK_API_BASE_URL}${href}`, filename).catch((err) => {
            console.error('Download failed:', err);
            alert(err instanceof Error ? err.message : 'Download failed');
          });
        }}
      >
        {children}
      </a>
    );
  },
};

const AgentWorkspace: React.FC<AgentWorkspaceProps> = ({
  sessionId,
  platform,
  userEmail,
  onLogout,
  selectedGoogleAdsCustomer,
  selectedAdvertiserId,
  completedSteps = [],
  onNavigateToStep,
  onStepComplete
}) => {
  // Step tracking (Steps 2-4 are handled in ChatInterfaceNew)
  const [currentStep, setCurrentStep] = useState(5); // Start at Generate Scenarios
  const [step5Complete, setStep5Complete] = useState(false); // Generate scenarios
  const [step6Complete, setStep6Complete] = useState(false); // Recommend strategy
  const [step7Complete, setStep7Complete] = useState(false); // Propose actions

  // Chat state
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Upload destination selection (for Step 6)
  const [useSameDestination, setUseSameDestination] = useState<boolean | null>(null);
  const [uploadMode, setUploadMode] = useState<'upload' | 'download' | null>(null);
  const [uploadPartnerId, setUploadPartnerId] = useState('');
  const [uploadAdvertiserId, setUploadAdvertiserId] = useState('');
  const [showUploadSelection, setShowUploadSelection] = useState(false);
  const [uploadPartners, setUploadPartners] = useState<any[]>([]);
  const [uploadAdvertisers, setUploadAdvertisers] = useState<any[]>([]);
  const [loadingUploadPartners, setLoadingUploadPartners] = useState(false);
  const [loadingUploadAdvertisers, setLoadingUploadAdvertisers] = useState(false);
  const [originalPartnerId, setOriginalPartnerId] = useState('');

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Update current step based on completion
  useEffect(() => {
    if (!step5Complete) {
      setCurrentStep(5);
    } else if (!step6Complete) {
      setCurrentStep(6);
    } else if (!step7Complete) {
      setCurrentStep(7);
    }
  }, [step5Complete, step6Complete, step7Complete]);

  // Notify parent when steps are completed
  useEffect(() => {
    if (step5Complete && onStepComplete) {
      onStepComplete(5);
    }
  }, [step5Complete, onStepComplete]);

  useEffect(() => {
    if (step6Complete && onStepComplete) {
      onStepComplete(6);
    }
  }, [step6Complete, onStepComplete]);

  useEffect(() => {
    if (step7Complete && onStepComplete) {
      onStepComplete(7);
    }
  }, [step7Complete, onStepComplete]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadUploadPartners = useCallback(async () => {
    setLoadingUploadPartners(true);
    try {
      const credsStr = sessionStorage.getItem('oauth_credentials');
      if (!credsStr) {
        console.error('No OAuth credentials found');
        return;
      }

      const creds = JSON.parse(credsStr);
      const response = await fetch(`${ADK_API_BASE_URL}/oauth/dv360/partners`, {
        headers: {
          'Authorization': `Bearer ${creds.access_token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUploadPartners(data || []);
      }
    } catch (error) {
      console.error('Failed to load upload partners:', error);
    } finally {
      setLoadingUploadPartners(false);
    }
  }, []);

  const loadUploadAdvertisers = useCallback(async (partnerId: string) => {
    if (!partnerId) return;

    setLoadingUploadAdvertisers(true);
    try {
      const credsStr = sessionStorage.getItem('oauth_credentials');
      if (!credsStr) {
        console.error('No OAuth credentials found');
        return;
      }

      const creds = JSON.parse(credsStr);
      const response = await fetch(`${ADK_API_BASE_URL}/oauth/dv360/advertisers?partner_id=${partnerId}`, {
        headers: {
          'Authorization': `Bearer ${creds.access_token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUploadAdvertisers(data || []);
      }
    } catch (error) {
      console.error('Failed to load upload advertisers:', error);
    } finally {
      setLoadingUploadAdvertisers(false);
    }
  }, []);

  // Load upload partners when Step 7 starts (for DV360)
  useEffect(() => {
    if (currentStep === 7 && platform === 'dv360' && !step7Complete) {
      loadUploadPartners();
      // Store original partner ID from props if available
      // We'll need to fetch it from the initial selection
    }
  }, [currentStep, platform, step7Complete, loadUploadPartners]);

  // When user chooses to use same destination, pre-populate the fields
  useEffect(() => {
    if (useSameDestination === true && selectedAdvertiserId) {
      setUploadAdvertiserId(selectedAdvertiserId);
      // We need to fetch the partner ID associated with this advertiser
      // For now, user will select from dropdowns
    }
  }, [useSameDestination, selectedAdvertiserId]);

  // Load advertisers when upload partner changes
  useEffect(() => {
    if (uploadPartnerId) {
      loadUploadAdvertisers(uploadPartnerId);
    }
  }, [uploadPartnerId, loadUploadAdvertisers]);

  const handleSendMessage = useCallback(async (messageText?: string) => {
    const text = messageText || inputValue.trim();
    if (!text || !sessionId) return;

    const { userId } = getSessionInfo();
    if (!userId) return;

    // A workflow trigger is machine addressing, not something anyone typed.
    // Echoing it put several lines of `RUN_MEDIA_PLAN_SCENARIOS\nSession ID: …`
    // at the top of the step, taking a full-width row from the content it was
    // fetching.
    if (!isTriggerCommand(text)) {
      const userMessage: Message = {
        id: Date.now().toString(),
        type: 'user',
        content: text,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, userMessage]);
    }
    setInputValue('');
    setIsLoading(true);

    try {
      // Determine the appropriate workflow trigger and format message based on current step
      let messageWithSessionId = text;

      if (text.startsWith('RUN_') && text.includes('Session ID:')) {
        // Initial trigger messages already formatted (from auto-start)
        messageWithSessionId = text;
      } else if (currentStep === 5) {
        // Step 5: Only include customer ID for Google Ads platform
        // DV360 uses fixed customer ID from env variable for reach forecasting
        if (platform === 'google_ads') {
          messageWithSessionId = `RUN_MEDIA_PLAN_SCENARIOS\nSession ID: ${sessionId}\nGoogle Ads Customer ID: ${selectedGoogleAdsCustomer}\nUser Message: ${text}`;
        } else {
          messageWithSessionId = `RUN_MEDIA_PLAN_SCENARIOS\nSession ID: ${sessionId}\nUser Message: ${text}`;
        }
      } else if (currentStep === 6) {
        messageWithSessionId = `RUN_RECOMMEND_STRATEGY\nSession ID: ${sessionId}\nUser Message: ${text}`;
      } else if (currentStep === 7) {
        // Use uploadAdvertiserId (selected in Step 7 UI) instead of original selectedAdvertiserId
        messageWithSessionId = `RUN_CAMPAIGN_MANAGER\nSession ID: ${sessionId}\nAdvertiser ID: ${uploadAdvertiserId}\nUser Message: ${text}`;
      } else {
        // UNREACHABLE today, and it must stay that way. The effect above sets
        // currentStep to 5, 6 or 7 and nothing else assigns it, so there is no
        // path here — which is why this is a throw rather than a message.
        //
        // It used to build `Session ID: …\nUser Message: …` with no RUN_
        // command. An uncommanded message is refused by the backend router now
        // (it once let a stray word run a later step and overwrite that step's
        // artifact), so a new step added without a branch here would fail in
        // front of a user with a puzzling "I could not tell which step that
        // request belongs to" instead of at the point the step was added.
        //
        // Adding step 8? Add its branch above.
        console.error(
          `AgentWorkspace: no command mapped for step ${currentStep}; ` +
          'add a branch in handleSendMessage'
        );
        throw new Error(`No agent command is mapped for step ${currentStep}`);
      }

      const stream = await sendADKMessageStreaming(ADK_APP_NAME, userId, sessionId, messageWithSessionId, platform);
      const reader = stream.getReader();
      const decoder = new TextDecoder();

      let assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: '',
        timestamp: new Date(),
        isStreaming: true
      };
      setMessages(prev => [...prev, assistantMessage]);

      // Buffer for incomplete SSE lines split across TCP chunks
      let sseBuffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        sseBuffer += decoder.decode(value, { stream: true });
        const lines = sseBuffer.split('\n');
        // Keep the last (potentially incomplete) line in the buffer
        sseBuffer = lines.pop() ?? '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.type === 'auth_expired') {
                onLogout();
                return;
              } else if (data.type === 'content' && data.text) {
                assistantMessage.content += data.text;
                setMessages(prev =>
                  prev.map(msg =>
                    msg.id === assistantMessage.id
                      ? { ...msg, content: assistantMessage.content }
                      : msg
                  )
                );

                // Completion is signalled by an explicit step_complete event
                // (handled below), not by matching this prose. Searching the
                // text meant rewording a backend line silently stopped the
                // step advancing, with the proceed button apparently dead.
              } else if (data.type === 'step_complete') {
                // The backend names the step it finished; no prose matching.
                if (data.step === 5) setStep5Complete(true);
                else if (data.step === 6) setStep6Complete(true);
                else if (data.step === 7) setStep7Complete(true);
              } else if (data.type === 'message_end') {
                assistantMessage.isStreaming = false;
                setMessages(prev =>
                  prev.map(msg =>
                    msg.id === assistantMessage.id
                      ? { ...msg, isStreaming: false }
                      : msg
                  )
                );
              }
            } catch (e) {
              console.error('Failed to parse SSE data:', e);
            }
          }
        }
      }
    } catch (error: any) {
      if (isAuthExpiredError(error)) {
        // The auth dependency rejects BEFORE the stream starts, so the
        // backend's `auth_expired` SSE event cannot fire — without this the
        // user saw "Failed to send message: Unauthorized" and had no way
        // forward. Route it into the same re-login path.
        console.warn('Session expired; signing out to re-authenticate.');
        onLogout();
        return;
      }
      console.error('Failed to send message:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: `Error: ${error.message || 'Failed to send message'}`,
        timestamp: new Date(),
        isStreaming: false
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  }, [inputValue, sessionId, currentStep, platform, selectedGoogleAdsCustomer, uploadAdvertiserId]);

  // Auto-start agent when currentStep changes
  const hasAutoStarted = useRef<number>(0);

  useEffect(() => {
    if (!sessionId || hasAutoStarted.current === currentStep) return;

    // Auto-trigger agent when step changes
    if (currentStep === 5 && !step5Complete) {
      hasAutoStarted.current = 5;
      // Only include customer ID for Google Ads; DV360 uses env variable
      if (platform === 'google_ads') {
        handleSendMessage(`RUN_MEDIA_PLAN_SCENARIOS\nSession ID: ${sessionId}\nGoogle Ads Customer ID: ${selectedGoogleAdsCustomer}`);
      } else {
        handleSendMessage(`RUN_MEDIA_PLAN_SCENARIOS\nSession ID: ${sessionId}`);
      }
    } else if (currentStep === 6 && !step6Complete) {
      hasAutoStarted.current = 6;
      handleSendMessage(`RUN_RECOMMEND_STRATEGY\nSession ID: ${sessionId}`);
    } else if (currentStep === 7 && !step7Complete) {
      // Don't auto-start Step 7 - show configuration UI first
      hasAutoStarted.current = 7;
      setShowUploadSelection(true);
      // Reset upload mode states when entering Step 7
      setUploadMode(null);
      setUseSameDestination(null);
    }
  }, [currentStep, sessionId, platform, selectedGoogleAdsCustomer, uploadAdvertiserId, step5Complete, step6Complete, step7Complete, handleSendMessage]);

  // Model B: upload the generated SDFs to DV360 as the logged-in user (their token),
  // so it targets the advertisers THEY can access — not a shared service identity.
  const uploadSdfToDv360 = useCallback(async (advertiserId: string) => {
    if (!advertiserId || !sessionId) return;
    let token = '';
    try {
      const credsStr = sessionStorage.getItem('oauth_credentials');
      if (credsStr) token = JSON.parse(credsStr).access_token || '';
    } catch {
      /* ignore */
    }
    if (!token) {
      setMessages(prev => [...prev, {
        id: `${Date.now()}-noauth`, type: 'assistant',
        content: '⚠️ Your session has no active sign-in token, so the upload can\'t run as you. Please log in again, then use Generate and Upload. The files are available to download above.',
        timestamp: new Date(),
      }]);
      return;
    }
    const stamp = Date.now();
    setMessages(prev => [...prev, {
      id: `${stamp}-uploading`, type: 'assistant',
      content: '📤 Uploading SDF files to DV360…', timestamp: new Date(),
    }]);
    let text: string;
    try {
      const res = await fetch(`${ADK_API_BASE_URL}/oauth/dv360/upload-sdf`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, advertiser_id: advertiserId }),
      });
      // A plan past its retention limit is gone, downloads included.
      const data = res.status === 410 ? { status: 'GONE', detail: (await planGoneError(res)).message } : await res.json();
      if (data.status === 'GONE') {
        text = `⚠️ ${data.detail}`;
      } else if (data.status === 'SUCCESS') {
        const task = data.resource_name || data.operation_name || '';
        text = `✅ SDF files uploaded to DV360 advertiser ${advertiserId}. Upload task: ${task}. Track it in DV360 → Structured Data Files.`;
      } else if (data.status === 'SKIPPED') {
        text = `ℹ️ Upload skipped: ${data.message || 'not configured'}. The files are available to download above.`;
      } else {
        const detail = data.message || (data.error && data.error.message) || data.detail || 'unknown error';
        text = `⚠️ DV360 upload ${String(data.status || 'failed').toLowerCase()}: ${detail}. You can download the files above and upload manually.`;
      }
    } catch (e) {
      text = `⚠️ DV360 upload request failed: ${e}. The files are available to download above.`;
    }
    setMessages(prev => [...prev, {
      id: `${Date.now()}-uploaded`, type: 'assistant', content: text, timestamp: new Date(),
    }]);
  }, [sessionId]);

  // Generated files to offer for download on the final completion card (deduped).
  const downloadLinks = React.useMemo(() => {
    const seen = new Set<string>();
    const out: { filename: string; href: string }[] = [];
    for (const m of messages) {
      for (const l of extractDownloadLinks(m.content)) {
        if (!seen.has(l.href)) {
          seen.add(l.href);
          out.push(l);
        }
      }
    }
    return out;
  }, [messages]);

  return (
    <div className="app-container">
      <ParticleBackground />
      <TopNavBar
        currentStep={currentStep}
        isAuthenticated={true}
        userEmail={userEmail}
        onLogout={onLogout}
        completedSteps={completedSteps}
        onNavigateToStep={onNavigateToStep}
      />

      <div className="workspace-layout">
        {/* Full-width chat interface - no split screen */}
        <div className="chat-panel">
          <div className="messages-container">
            <AnimatePresence>
              {messages.map((message) => {
                // Extract chart data from message content
                const { charts, cleanedContent } = extractChartData(message.content);
                // Scenarios render as a card grid rather than fifteen
                // stacked headings. Hide the block until it has fully
                // streamed, so half a payload never shows as raw JSON.
                const scenarioPayload = parseScenarios(cleanedContent);
                const prose = scenarioPayload
                  ? stripScenarios(cleanedContent)
                  : cleanedContent.replace(/```json:scenarios[\s\S]*$/, '');

                return (
                  <motion.div
                    key={message.id}
                    className={`message ${message.type}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="message-content">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm, remarkBreaks]}
                        className="markdown-content"
                        components={markdownComponents}
                      >
                        {prose}
                      </ReactMarkdown>
                      {scenarioPayload && <ScenarioCards payload={scenarioPayload} />}
                      {charts.map((chartData, idx) => (
                        <ReachCurveChart
                          key={`chart-${message.id}-${idx}`}
                          scenarios={chartData.scenarios}
                          data={chartData.data}
                          userBudget={chartData.userBudget}
                          currencyCode={chartData.currencyCode}
                          winnerScenarioId={chartData.winnerScenarioId}
                        />
                      ))}
                      {message.isStreaming && <span className="cursor">▊</span>}
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
            {isLoading && (
              <div className="loading-indicator">
                <Loader className="spinner" size={24} />
                <span>AI Agent is working...</span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="input-container">
            {/* Steps 5 and 6 advance on a click rather than a typed "yes".
                The button sends the same `proceed` word _is_proceed_message()
                already accepts, so it reuses a path that works and the typed
                route keeps working for anyone who prefers it.
                Gated on the last message having finished streaming, so it
                cannot fire against a half-run step. */}
            {!isLoading && messages.length > 0 && !messages[messages.length - 1].isStreaming && (
              <>
                {currentStep === 5 && !step5Complete && (
                  <button
                    onClick={() => handleSendMessage('proceed')}
                    className="continue-button"
                    style={{ width: '100%', marginBottom: '16px' }}
                  >
                    Proceed to Recommend Strategy →
                  </button>
                )}
                {currentStep === 6 && !step6Complete && (
                  <button
                    onClick={() => handleSendMessage('proceed')}
                    className="continue-button"
                    style={{ width: '100%', marginBottom: '16px' }}
                  >
                    Proceed to Propose Actions →
                  </button>
                )}
              </>
            )}

            {/* Step 7 Configuration for DV360 */}
            {showUploadSelection && currentStep === 7 && platform === 'dv360' && !step7Complete && (
              <div className="agent-output-container" style={{ marginBottom: '16px' }}>
                <div className="agent-output-header">
                  <span>📤 Campaign Files Generation</span>
                </div>
                <div className="agent-output-content">
                  {/* Step 1: Choose upload mode */}
                  {uploadMode === null && (
                    <>
                      <p style={{ marginBottom: '16px', color: '#666', fontWeight: '500' }}>
                        How would you like to handle the campaign files?
                      </p>
                      <div style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
                        <button
                          onClick={() => setUploadMode('upload')}
                          className="continue-button"
                          style={{ width: '100%' }}
                        >
                          📤 Generate and Upload to DV360
                        </button>
                        <button
                          onClick={() => setUploadMode('download')}
                          className="continue-button"
                          style={{ width: '100%', background: '#6B7280' }}
                        >
                          📥 Just Generate Files for Download
                        </button>
                      </div>
                    </>
                  )}

                  {/* Step 2: If upload mode, ask about destination */}
                  {uploadMode === 'upload' && useSameDestination === null && (
                    <>
                      <p style={{ marginBottom: '16px', color: '#666', fontWeight: '500' }}>
                        Upload to the same advertiser or a different one?
                      </p>
                      <div style={{ marginBottom: '12px', padding: '12px', background: '#F3F4F6', borderRadius: '8px' }}>
                        <p style={{ fontSize: '14px', color: '#374151', margin: 0 }}>
                          <strong>Currently selected:</strong><br />
                          Advertiser ID: {selectedAdvertiserId || 'Not selected'}
                        </p>
                      </div>
                      <div style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
                        <button
                          onClick={() => {
                            setUseSameDestination(true);
                            setUploadAdvertiserId(selectedAdvertiserId || '');
                          }}
                          className="continue-button"
                          style={{ width: '100%' }}
                        >
                          ✓ Use Same Advertiser
                        </button>
                        <button
                          onClick={() => setUseSameDestination(false)}
                          className="continue-button"
                          style={{ width: '100%', background: '#6B7280' }}
                        >
                          → Choose Different Advertiser
                        </button>
                        <button
                          onClick={() => setUploadMode(null)}
                          style={{
                            padding: '8px 16px',
                            background: 'transparent',
                            border: 'none',
                            color: '#6B7280',
                            cursor: 'pointer',
                            fontSize: '14px'
                          }}
                        >
                          ← Back
                        </button>
                      </div>
                    </>
                  )}

                  {/* Step 3: If different destination, show dropdowns */}
                  {uploadMode === 'upload' && useSameDestination === false && (
                    <>
                      <p style={{ marginBottom: '16px', color: '#666', fontWeight: '500' }}>
                        Select DV360 Partner and Advertiser for upload
                      </p>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {/* Partner Selection */}
                        <div className="form-group">
                          <label>Partner</label>
                          {loadingUploadPartners ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px' }}>
                              <Loader className="spinner" size={16} />
                              <span>Loading partners...</span>
                            </div>
                          ) : (
                            <select
                              value={uploadPartnerId}
                              onChange={(e) => {
                                setUploadPartnerId(e.target.value);
                                setUploadAdvertiserId(''); // Reset advertiser when partner changes
                              }}
                              style={{
                                width: '100%',
                                padding: '12px',
                                border: '1px solid #E5E7EB',
                                borderRadius: '8px',
                                fontSize: '14px'
                              }}
                            >
                              <option value="">Select a partner...</option>
                              {uploadPartners.map((partner) => (
                                <option key={partner.partner_id} value={partner.partner_id}>
                                  {partner.display_name} (ID: {partner.partner_id})
                                </option>
                              ))}
                            </select>
                          )}
                        </div>

                        {/* Advertiser Selection */}
                        {uploadPartnerId && (
                          <div className="form-group">
                            <label>Advertiser</label>
                            {loadingUploadAdvertisers ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px' }}>
                                <Loader className="spinner" size={16} />
                                <span>Loading advertisers...</span>
                              </div>
                            ) : (
                              <select
                                value={uploadAdvertiserId}
                                onChange={(e) => setUploadAdvertiserId(e.target.value)}
                                style={{
                                  width: '100%',
                                  padding: '12px',
                                  border: '1px solid #E5E7EB',
                                  borderRadius: '8px',
                                  fontSize: '14px'
                                }}
                              >
                                <option value="">Select an advertiser...</option>
                                {uploadAdvertisers.map((advertiser) => (
                                  <option key={advertiser.advertiser_id} value={advertiser.advertiser_id}>
                                    {advertiser.display_name} (ID: {advertiser.advertiser_id})
                                  </option>
                                ))}
                              </select>
                            )}
                          </div>
                        )}

                        <button
                          onClick={() => setUseSameDestination(null)}
                          style={{
                            padding: '8px 16px',
                            background: 'transparent',
                            border: 'none',
                            color: '#6B7280',
                            cursor: 'pointer',
                            fontSize: '14px'
                          }}
                        >
                          ← Back
                        </button>
                      </div>
                    </>
                  )}

                  {/* Final action button */}
                  {((uploadMode === 'upload' && useSameDestination !== null && uploadAdvertiserId) ||
                    uploadMode === 'download') && (
                    <div style={{ marginTop: '16px' }}>
                      <button
                        onClick={async () => {
                          setShowUploadSelection(false);
                          // Always generate the files server-side first (download_only
                          // means "don't server-upload" — files land in storage).
                          // The advertiser is the one the upload targets, so the
                          // SDF CSVs and the upload agree.
                          await handleSendMessage(campaignManagerGenerateTrigger(sessionId, {
                            advertiserId: uploadMode === 'upload' ? uploadAdvertiserId : selectedAdvertiserId,
                            mode: 'download_only',
                          }));
                          if (uploadMode !== 'download') {
                            // Model B: upload as the logged-in user (their DV360 access).
                            await uploadSdfToDv360(uploadAdvertiserId);
                          }
                        }}
                        className="continue-button"
                        style={{ width: '100%' }}
                      >
                        {uploadMode === 'download' ? '📥 Generate Files for Download' : '📤 Generate and Upload Campaign Files'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 7 Configuration for Google Ads */}
            {showUploadSelection && currentStep === 7 && platform === 'google_ads' && !step7Complete && (
              <div className="agent-output-container" style={{ marginBottom: '16px' }}>
                <div className="agent-output-header">
                  <span>📤 Campaign Files Generation</span>
                </div>
                <div className="agent-output-content">
                  {/* Choose mode: Scripts or Download */}
                  {uploadMode === null && (
                    <>
                      <p style={{ marginBottom: '16px', color: '#666', fontWeight: '500' }}>
                        How would you like to handle the campaign configuration?
                      </p>
                      <div style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
                        <button
                          onClick={() => setUploadMode('upload')}
                          className="continue-button"
                          style={{ width: '100%' }}
                        >
                          📜 Generate Google Ads Scripts
                        </button>
                        <button
                          onClick={() => setUploadMode('download')}
                          className="continue-button"
                          style={{ width: '100%', background: '#6B7280' }}
                        >
                          📥 Just Generate Configuration for Download
                        </button>
                      </div>
                    </>
                  )}

                  {/* Final action button */}
                  {uploadMode !== null && (
                    <div style={{ marginTop: uploadMode === null ? '0' : '16px' }}>
                      {uploadMode !== null && (
                        <button
                          onClick={() => setUploadMode(null)}
                          style={{
                            padding: '8px 16px',
                            background: 'transparent',
                            border: 'none',
                            color: '#6B7280',
                            cursor: 'pointer',
                            fontSize: '14px',
                            marginBottom: '12px'
                          }}
                        >
                          ← Back
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setShowUploadSelection(false);
                          if (uploadMode === 'download') {
                            handleSendMessage(campaignManagerGenerateTrigger(sessionId, { mode: 'download_only' }));
                          } else {
                            handleSendMessage(campaignManagerGenerateTrigger(sessionId, {
                              googleAdsCustomerId: selectedGoogleAdsCustomer,
                            }));
                          }
                        }}
                        className="continue-button"
                        style={{ width: '100%' }}
                      >
                        {uploadMode === 'download' ? '📥 Generate Configuration for Download' : '📜 Generate Google Ads Scripts'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Show completion + download when all steps complete */}
            {step7Complete && (
              <div className="success-card" style={{ marginBottom: '16px' }}>
                <h3>All Steps Complete! 🎉</h3>
                <p>Your campaign plan is ready.{downloadLinks.length > 0 ? ' Download the generated files:' : ' Review all outputs above.'}</p>
                {downloadLinks.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
                    {downloadLinks.map((l) => (
                      // A button, not an <a href>: /download-file now requires a
                      // bearer token, and a browser navigation cannot carry a
                      // header — the link would simply be refused with a 401.
                      // downloadProtectedFile fetches it with credentials and
                      // hands the bytes over as a blob.
                      <button
                        key={l.href}
                        type="button"
                        onClick={() => {
                          downloadProtectedFile(l.href, l.filename).catch((err) => {
                            console.error('Download failed:', err);
                            alert(err instanceof Error ? err.message : 'Download failed');
                          });
                        }}
                        className="continue-button"
                        style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                      >
                        ⬇ {l.filename}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="input-wrapper">
              <input
                ref={inputRef}
                type="text"
                className="message-input"
                placeholder="Ask follow-up questions or provide feedback..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                disabled={isLoading}
              />
              <button
                className="send-button"
                onClick={() => handleSendMessage()}
                disabled={isLoading || !inputValue.trim()}
              >
                <Send size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentWorkspace;
